import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const EMPTY_PERSONAL = { first_name:'', last_name:'', email:'', phone:'', nic:'', date_of_birth:'', gender:'', address:'', emergency_contact_name:'', emergency_contact_phone:'', marital_status:'', nationality:'Sri Lankan', status:'Active' };
const EMPTY_DESG = { designation_id:'', employment_type:'', start_date:'', salary:'', reporting_to:'' };

// ✅ Defined OUTSIDE component — never recreated on re-render
const Field = ({ label, children }) => (
  <div className="form-group">
    {label && <label>{label}</label>}
    {children}
  </div>
);

export default function AddEmployee() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [personal, setPersonal] = useState(EMPTY_PERSONAL);
  const [desg, setDesg] = useState(EMPTY_DESG);
  const [designations, setDesignations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/designations').then(r => setDesignations(r.data.data));
    api.get('/employees').then(r => setEmployees(r.data.data));
  }, []);

  // ✅ useCallback prevents new function reference on every render
  const setP = useCallback((k, v) => setPersonal(p => ({ ...p, [k]: v })), []);
  const setD = useCallback((k, v) => setDesg(d => ({ ...d, [k]: v })), []);

  const handleSubmit = async () => {
    setSaving(true); setError('');
    try {
      const r = await api.post('/employees', { personal, initialDesignation: desg.designation_id ? desg : null });
      nav(`/employees/${r.data.data.id}`);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save.');
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      {/* Stepper */}
      <div style={{ display:'flex', alignItems:'center', gap:0, marginBottom:28 }}>
        {[1,2,3].map((s,i) => (
          <React.Fragment key={s}>
            <div style={{ display:'flex', alignItems:'center', gap:8, cursor: s <= step ? 'pointer' : 'default' }} onClick={() => s < step && setStep(s)}>
              <div style={{ width:32, height:32, borderRadius:'50%', background: step >= s ? 'var(--indigo)' : 'var(--slate-100)', color: step >= s ? '#fff' : 'var(--slate-400)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, transition:'all .2s' }}>{s}</div>
              <span style={{ fontSize:13, fontWeight: step === s ? 600 : 400, color: step === s ? 'var(--navy)' : 'var(--slate-400)' }}>{['Personal Info','Position','Confirm'][i]}</span>
            </div>
            {i < 2 && <div style={{ flex:1, height:2, background: step > s ? 'var(--indigo)' : 'var(--slate-100)', margin:'0 12px', transition:'all .2s' }} />}
          </React.Fragment>
        ))}
      </div>

      <div className="card">
        <div className="card-body">
          {error && <div className="alert alert-error">{error}</div>}

          {step === 1 && (
            <>
              <h3 style={{marginBottom:20,color:'var(--navy)',fontSize:15,fontWeight:600}}>Personal Details</h3>
              <div className="form-grid">
                <Field label="First Name *">
                  <input value={personal.first_name} onChange={e => setP('first_name', e.target.value)} placeholder="e.g. Kamal" />
                </Field>
                <Field label="Last Name *">
                  <input value={personal.last_name} onChange={e => setP('last_name', e.target.value)} placeholder="e.g. Perera" />
                </Field>
                <Field label="Email *">
                  <input type="email" value={personal.email} onChange={e => setP('email', e.target.value)} placeholder="kamal@company.lk" />
                </Field>
                <Field label="Phone">
                  <input value={personal.phone} onChange={e => setP('phone', e.target.value)} placeholder="+94 77 123 4567" />
                </Field>
                <Field label="NIC Number">
                  <input value={personal.nic} onChange={e => setP('nic', e.target.value)} placeholder="200012345678" />
                </Field>
                <Field label="Date of Birth">
                  <input type="date" value={personal.date_of_birth} onChange={e => setP('date_of_birth', e.target.value)} />
                </Field>
                <Field label="Gender">
                  <select value={personal.gender} onChange={e => setP('gender', e.target.value)}>
                    <option value="">Select</option>
                    <option>Male</option><option>Female</option><option>Other</option>
                  </select>
                </Field>
                <Field label="Marital Status">
                  <select value={personal.marital_status} onChange={e => setP('marital_status', e.target.value)}>
                    <option value="">Select</option>
                    <option>Single</option><option>Married</option><option>Divorced</option><option>Widowed</option>
                  </select>
                </Field>
                <Field label="Nationality">
                  <input value={personal.nationality} onChange={e => setP('nationality', e.target.value)} />
                </Field>
                <div className="form-group full">
                  <label>Address</label>
                  <textarea value={personal.address} onChange={e => setP('address', e.target.value)} placeholder="No. 12, Main Street, Colombo" />
                </div>
                <Field label="Emergency Contact Name">
                  <input value={personal.emergency_contact_name} onChange={e => setP('emergency_contact_name', e.target.value)} />
                </Field>
                <Field label="Emergency Contact Phone">
                  <input value={personal.emergency_contact_phone} onChange={e => setP('emergency_contact_phone', e.target.value)} />
                </Field>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h3 style={{marginBottom:20,color:'var(--navy)',fontSize:15,fontWeight:600}}>Initial Position</h3>
              <div className="form-grid">
                <Field label="Designation *">
                  <select value={desg.designation_id} onChange={e => setD('designation_id', e.target.value)}>
                    <option value="">Select designation</option>
                    {Object.entries(designations.reduce((a,d) => ({ ...a, [d.department]: [...(a[d.department]||[]), d] }), {})).map(([dept,list]) => (
                      <optgroup key={dept} label={dept}>{list.map(d => <option key={d.id} value={d.id}>{d.title}</option>)}</optgroup>
                    ))}
                  </select>
                </Field>
                <Field label="Employment Type *">
                  <select value={desg.employment_type} onChange={e => setD('employment_type', e.target.value)}>
                    <option value="">Select type</option>
                    <option>Intern</option><option>Permanent</option><option>Contract</option><option>Part-Time</option>
                  </select>
                </Field>
                <Field label="Start Date *">
                  <input type="date" value={desg.start_date} onChange={e => setD('start_date', e.target.value)} />
                </Field>
                <Field label="Monthly Salary (LKR)">
                  <input type="number" value={desg.salary} onChange={e => setD('salary', e.target.value)} placeholder="e.g. 85000" />
                </Field>
                <Field label="Reports To">
                  <select value={desg.reporting_to} onChange={e => setD('reporting_to', e.target.value)}>
                    <option value="">None / TBD</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.current_designation || '?'}</option>)}
                  </select>
                </Field>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h3 style={{marginBottom:20,color:'var(--navy)',fontSize:15,fontWeight:600}}>Confirm Details</h3>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                {[['Name',`${personal.first_name} ${personal.last_name}`],['Email',personal.email],['Phone',personal.phone],['NIC',personal.nic],['Gender',personal.gender],['Nationality',personal.nationality]].map(([k,v]) => (
                  <div key={k} style={{background:'var(--slate-50)',borderRadius:8,padding:'10px 14px'}}>
                    <div style={{fontSize:11,color:'var(--slate-500)',fontWeight:600}}>{k}</div>
                    <div style={{fontSize:13,fontWeight:500,color:'var(--navy)',marginTop:2}}>{v || '—'}</div>
                  </div>
                ))}
              </div>
              {desg.designation_id && (
                <>
                  <div style={{margin:'16px 0 8px',fontSize:13,fontWeight:600,color:'var(--navy)'}}>Position</div>
                  <div style={{background:'var(--indigo-pale)',borderRadius:8,padding:'12px 16px',fontSize:13,color:'var(--indigo)'}}>
                    {designations.find(d => String(d.id) === String(desg.designation_id))?.title} · {desg.employment_type} · from {desg.start_date}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          {step > 1 && <button className="btn btn-outline" onClick={() => setStep(s => s - 1)}>Back</button>}
          {step < 3 && <button className="btn btn-primary" onClick={() => setStep(s => s + 1)} disabled={step===1 && (!personal.first_name||!personal.email)}>Next</button>}
          {step === 3 && <button className="btn btn-success" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Create Employee'}</button>}
        </div>
      </div>
    </div>
  );
}
